// ============================================================
// packageRounding.js
// The single home for "this SKU is sold in packages of N", and for "and this
// one should actually be rounded to them on a quote".
//
// WHAT THIS REPLACES
// ------------------
// A ROUND_UP_RULES array — a per-SKU package-size table — used to live
// duplicated verbatim in NewDocument.js and EditDocument.js. It failed twice,
// once in each way a hard-coded table can:
//
//   1. A NUMBER WENT STALE. The rule for HRBK carried roundTo: 625, copied from
//      the galvanized HR9AP rule, against a product that ships 420 per bag.
//      Black hog rings rounded to the wrong package and nothing said so.
//   2. A STRING STOPPED MATCHING. Both barbed wire rules matched on the
//      description keywords "barbed wire" / "barb wire". The product reads
//      "Barbwire Galv (1,320' per roll - CAN ONLY BE SOLD IN FULL ROLLS)".
//      "Barbwire" is one word, so neither keyword matched, and the one item the
//      description explicitly says can only be sold in full rolls was the one
//      item that never rounded — silently.
//
// So the size now comes from the product record itself, where the owner already
// maintains it in Inventory, and where it cannot drift from the product it
// describes. There is deliberately NO fallback table and NO parsing of the
// description string.
//
// ── TWO FIELDS, BECAUSE THERE ARE TWO INTENTIONS ──
//
// packQty alone was not enough, and treating it as enough was a real bug.
//
// packQty exists so Inventory can say "2 full bags plus 60 loose" — which is a
// perfectly good reason to fill it in on a hundred products that must NEVER be
// rounded on a customer's quote. When rounding keyed off packQty alone, filling
// in an inventory hint silently armed quantity rounding on that product. One
// field was answering two different questions.
//
// So the question "what does a package hold" (packQty) is now separate from the
// question "should a quote round this up to whole packages" (roundUpInQuotes, a
// boolean the owner ticks in Inventory). A line rounds only when BOTH are true:
// the product is marked, AND it has a usable package size. Marked with no size
// is a half-configured product and is reported by name; unmarked is silent,
// because unmarked is the normal case for nearly every product in the catalogue
// and saying so would bury the messages that matter.
//
// ── WHY getPackSize IS NOT GATED ──
//
// getPackSize answers a question of FACT: how many are in a package. The
// estimator results screen uses it for its per-row ↑N button, which is a
// salesperson deliberately rounding one row he is looking at, before the items
// are ever imported. That is a different act from "round everything on this
// document that should round", and gating the fact would also make that
// screen's tooltips lie. roundsInQuotes() is the POLICY, and it is what
// roundLineItems uses.
//
// Rounding is MANUAL. Nothing here runs on import, on save, or on a timer — a
// person presses a button and gets an answer, including an answer about what
// could not be done.
// ============================================================

/**
 * The package size for a line item, taken from its product's `packQty`.
 *
 * A statement of FACT about the packaging, independent of whether anything
 * should be rounded to it. Returns a positive number, or null when the size is
 * unusable: no SKU, no matching product, or a packQty that is unset, zero,
 * negative or unparseable.
 */
export function getPackSize(item, products) {
  const product = findProduct(item, products);
  if (!product) return null;
  return packSizeOf(product);
}

/** Has the owner marked this product to round up on quotes? */
export const roundsUpInQuotes = (product) => product?.roundUpInQuotes === true;

/**
 * THE POLICY: should this line be rounded by the Round Up button?
 *
 * Both halves, deliberately. The flag without a size cannot round to anything,
 * and the size without the flag is an inventory hint that was never meant to
 * change a customer's quantity.
 */
export function roundsInQuotes(item, products) {
  const product = findProduct(item, products);
  if (!product || !roundsUpInQuotes(product)) return null;
  return packSizeOf(product);
}

/** Next whole package at or above qty. */
export function roundToPackage(qty, packSize) {
  const q = parseFloat(qty) || 0;
  const size = parseFloat(packSize) || 0;
  if (size <= 0) return q;
  return Math.ceil(q / size) * size;
}

/**
 * Round every line item the owner has marked, and report every other outcome
 * that a person needs to hear about.
 *
 *   items       — a new array; untouched rows are passed through by reference
 *   changes     — [{ description, from, to }] for rows whose quantity moved
 *   missing     — [{ sku, description }] for rows MARKED to round that have no
 *                 usable pack size. A half-configured product: the owner asked
 *                 for rounding and the record cannot deliver it.
 *   skipped     — [{ sku, description }] for rows that resolve to NO product at
 *                 all, excluding the deliberate placeholders.
 *
 * WHAT IS DELIBERATELY SILENT: a product that has a pack size but is not marked
 * to round. That is the ordinary state of nearly every product in the
 * catalogue, and reporting it would drown the two messages above in noise.
 *
 * WHY `skipped` EXISTS. Template bundle lines are stored with sku 'TEMPLATE'
 * and productId null, so they match no product — they were neither rounded nor
 * reported, which is precisely the silent no-op this module was written to
 * eliminate, arriving through a door it was not watching. They are not made to
 * round here: their SHAPE is the real problem and fixing it is a different
 * change. But they are no longer invisible.
 *
 * '—' and 'NON-STOCK' are excluded from `skipped` because they are not
 * failures: the document editor writes them on purpose for free-text and
 * non-stock rows, which have no product and are not supposed to have one.
 * Text notes carry no quantity at all and are passed straight through.
 */
export function roundLineItems(lineItems, products) {
  const changes = [];
  const missing = [];
  const skipped = [];
  const seenMissing = new Set();
  const seenSkipped = new Set();

  const items = (lineItems || []).map(item => {
    // Text notes are printed prose, not goods. No quantity, nothing to round,
    // nothing to report.
    if (!item || item.isTextNote) return item;

    const product = findProduct(item, products);

    if (!product) {
      // Unresolvable. Report it unless it is one of the two placeholders the
      // editor writes on purpose.
      const sku = String(item.sku ?? '').trim();
      if (sku !== '—' && sku !== 'NON-STOCK') {
        const key = `${sku}|${item.description || ''}`;
        if (!seenSkipped.has(key)) {
          seenSkipped.add(key);
          skipped.push({ sku: sku || '(no SKU)', description: item.description || '' });
        }
      }
      return item;
    }

    if (!roundsUpInQuotes(product)) return item;   // not marked — silent, by design

    const packSize = packSizeOf(product);
    if (packSize === null) {
      // Marked to round, but the record cannot say what a package holds.
      const sku = item.sku || product.sku || '';
      if (!seenMissing.has(sku)) {
        seenMissing.add(sku);
        missing.push({ sku, description: product.description || item.description || '' });
      }
      return item;
    }

    const from = parseFloat(item.qty) || 0;
    const to = roundToPackage(from, packSize);
    if (to === from) return item;   // already an exact multiple

    changes.push({ description: item.description || item.sku || '', from, to });
    return { ...item, qty: to };
  });

  return { items, changes, missing, skipped };
}

/** A product's usable package size, or null. */
function packSizeOf(product) {
  const size = parseFloat(product?.packQty);
  return Number.isFinite(size) && size > 0 ? size : null;
}

/**
 * Resolve a line item to its product. productId first — it survives a SKU being
 * renamed — then the SKU, skipping the placeholders the document editor uses for
 * free-text and non-stock rows.
 */
function findProduct(item, products) {
  const list = products || [];
  if (!item) return null;
  if (item.productId) {
    const byId = list.find(p => p.id === item.productId);
    if (byId) return byId;
  }
  const sku = item.sku;
  if (!sku || sku === '—' || sku === 'NON-STOCK') return null;
  return list.find(p => p.sku === sku) || null;
}
